import {createContext, useContext, useMemo} from 'react'
import {
  type GestureResponderEvent,
  type Insets,
  type StyleProp,
  type View,
  type ViewStyle,
} from 'react-native'

import {useHaptics} from '#/lib/haptics'
import {atoms as a, useTheme} from '#/alf'
import {Button, type ButtonProps} from '#/components/Button'
import {type Props as SVGIconProps} from '#/components/icons/common'
import {Text, type TextProps} from '#/components/Typography'

export const DEFAULT_HITSLOP = {top: 5, bottom: 10, left: 10, right: 10}
const POST_CONTROL_MIN_SIZE = 30

const PostControlContext = createContext<{
  big?: boolean
  active?: boolean
  color?: {color: string}
}>({})
PostControlContext.displayName = 'PostControlContext'

// Base button style, which the the other ones extend
export function PostControlButton({
  ref,
  onPress,
  onLongPress,
  children,
  big,
  active,
  activeColor,
  activeStyle,
  selected,
  style: styleProp,
  ...props
}: Omit<ButtonProps, 'hitSlop'> & {
  ref?: React.Ref<React.ComponentRef<typeof View>>
  active?: boolean
  big?: boolean
  color?: string
  activeColor?: string
  activeStyle?: StyleProp<ViewStyle>
  selected?: boolean
  hitSlop?: Insets
}) {
  const t = useTheme()
  const playHaptic = useHaptics()

  const ctx = useMemo(
    () => ({
      big,
      active,
      color: {
        color: activeColor && active ? activeColor : t.palette.contrast_500,
      },
    }),
    [big, active, activeColor, t.palette.contrast_500],
  )

  const style = useMemo(
    () => [
      a.flex_row,
      a.align_center,
      a.gap_xs,
      a.bg_transparent,
      {
        minWidth: POST_CONTROL_MIN_SIZE,
        minHeight: POST_CONTROL_MIN_SIZE,
        padding: 5,
      },
    ],
    [],
  )

  const handlePress = useMemo(() => {
    if (!onPress) return
    return (evt: GestureResponderEvent) => {
      playHaptic('Light')
      onPress(evt)
    }
  }, [onPress, playHaptic])

  const handleLongPress = useMemo(() => {
    if (!onLongPress) return
    return (evt: GestureResponderEvent) => {
      playHaptic('Heavy')
      onLongPress(evt)
    }
  }, [onLongPress, playHaptic])

  const accessibilityState =
    selected === undefined
      ? props.accessibilityState
      : {...props.accessibilityState, selected}

  return (
    <Button
      ref={ref}
      onPress={handlePress}
      onLongPress={handleLongPress}
      {...props}
      style={[style, styleProp, active && activeStyle]}
      hoverStyle={t.atoms.bg_contrast_25}
      shape="round"
      variant="ghost"
      color="secondary"
      accessibilityState={accessibilityState}
      hitSlop={{
        ...DEFAULT_HITSLOP,
        ...(props.hitSlop || {}),
      }}>
      {typeof children === 'function' ? (
        args => (
          <PostControlContext.Provider value={ctx}>
            {children(args)}
          </PostControlContext.Provider>
        )
      ) : (
        <PostControlContext.Provider value={ctx}>
          {children}
        </PostControlContext.Provider>
      )}
    </Button>
  )
}

export function PostControlButtonIcon({
  icon: Comp,
  style,
  ...rest
}: SVGIconProps & {
  icon: React.ComponentType<SVGIconProps>
}) {
  const {big, color} = useContext(PostControlContext)

  return (
    <Comp
      style={[color, a.pointer_events_none, style]}
      {...rest}
      width={big ? 22 : 18}
    />
  )
}

export function PostControlButtonText({style, ...props}: TextProps) {
  const {big, active, color} = useContext(PostControlContext)

  return (
    <Text
      style={[
        color,
        a.user_select_none,
        big ? a.text_md : a.text_sm,
        active && a.font_semi_bold,
        // prevent layout shift on android
        {includeFontPadding: false, textAlignVertical: 'center'},
        style,
      ]}
      {...props}
    />
  )
}
